import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import { all_routes } from '../../router/all_routes'
import { getCBTAnswered, CBTQuestion, CBTAnsweredData } from '../../../api/courses'
import {
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  setStoredCertificateName,
  CertificateMeta,
} from '../../../utils/helpers'

const CBTReview = () => {
  const { id } = useParams<{ id: string }>()
  const [currentQuestion, setCurrentQuestion] = useState(0)

  // API data states
  const [questions, setQuestions] = useState<CBTQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userAnswerMap, setUserAnswerMap] = useState<Map<number, number>>(
    new Map(),
  )
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [cbtData, setCbtData] = useState<CBTAnsweredData | null>(null)
  const [certificateName, setCertificateName] = useState('')
  const [storedCertificateName, setStoredCertificateNameState] = useState('')
  const savedCertificateName =
    cbtData?.certificate_downloads?.name || storedCertificateName

  // Fetch CBT data from API
  useEffect(() => {
    const fetchCBTData = async () => {
      if (!id) {
        setError('Course ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Fetch CBT answered data
        const cbtResponse = await getCBTAnswered(id)
        setQuestions(cbtResponse.data.questions)
        setCbtData(cbtResponse.data)

        // Create user answer map from API data
        const map = new Map<number, number>()
        cbtResponse.data.user_answers.forEach((ua) => {
          map.set(parseInt(ua.question_id), parseInt(ua.answer_id))
        })
        setUserAnswerMap(map)
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load CBT data')
      } finally {
        setLoading(false)
      }
    }

    fetchCBTData()
  }, [id])

  useEffect(() => {
    setStoredCertificateNameState(getStoredCertificateName(cbtData?.id || id))
  }, [cbtData?.id, id])

  useEffect(() => {
    if (!savedCertificateName || !cbtData?.id) return
    if (
      cbtData.certificate_downloads?.certificate_number ||
      cbtData.certificate_downloads?.qr_url
    ) {
      return
    }

    prepareCertificateDownload(
      Number(cbtData.id),
      savedCertificateName,
      cbtData.score?.toString() || '',
      cbtData.min_passing_score || '',
    )
  }, [
    cbtData?.id,
    cbtData?.certificate_downloads?.name,
    cbtData?.certificate_downloads?.certificate_number,
    cbtData?.certificate_downloads?.qr_url,
    cbtData?.score,
    cbtData?.min_passing_score,
    savedCertificateName,
  ])

  const handleCertificateDownload = async () => {
    if (!cbtData) return

    const certificateMeta: CertificateMeta = {
      eventDates: cbtData?.certificate_event_dates || undefined,
      jplTotal: cbtData?.certificate_jpl_total || undefined,
      jplNote: cbtData?.certificate_jpl_note || undefined,
      speakers: cbtData?.certificate_speakers || undefined,
      materials: cbtData?.certificate_materials || undefined,
      location: cbtData?.certificate_location || undefined,
      signatureDate: undefined,
      certificateNumber:
        cbtData?.certificate_downloads?.certificate_number || undefined,
      qrUrl: cbtData?.certificate_downloads?.qr_url || undefined,
    }

    // Check if certificate_downloads exists and has a name
    if (savedCertificateName) {
      // Direct download with existing name
      await downloadCertificate(
        savedCertificateName,
        undefined, // customFileName
        undefined, // pdfUrl
        savedCertificateName, // userName
        cbtData?.title, // courseTitle
        true, // useAutoGeneration
        id ? parseInt(id) : undefined, // courseId
        undefined,
        true,
        cbtData.score.toString(),
        cbtData?.min_passing_score,
        certificateMeta,
      )
    } else {
      // Show modal for first time download (certificate_downloads is null)
      setShowCertificateModal(true)
    }
  }

  const confirmCertificateDownload = async () => {
    if (!cbtData) return

    setShowCertificateModal(false)
    setStoredCertificateName(cbtData?.id || id, certificateName)
    setStoredCertificateNameState(certificateName)

    const certificateMeta: CertificateMeta = {
      eventDates: cbtData?.certificate_event_dates || undefined,
      jplTotal: cbtData?.certificate_jpl_total || undefined,
      jplNote: cbtData?.certificate_jpl_note || undefined,
      speakers: cbtData?.certificate_speakers || undefined,
      materials: cbtData?.certificate_materials || undefined,
      location: cbtData?.certificate_location || undefined,
      signatureDate: undefined,
      certificateNumber:
        cbtData?.certificate_downloads?.certificate_number || undefined,
      qrUrl: cbtData?.certificate_downloads?.qr_url || undefined,
    }
    await downloadCertificate(
      certificateName,
      undefined, // customFileName
      undefined, // pdfUrl
      certificateName, // userName
      cbtData?.title, // courseTitle
      true, // useAutoGeneration
      id ? parseInt(id) : undefined, // courseId
      undefined,
      true,
      cbtData.score.toString(),
      cbtData?.min_passing_score,
      certificateMeta,
    )
  }

  // Loading state
  if (loading) {
    return (
      <>
        <Breadcrumb title="Review CBT Test" />
        <div className="content">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="card text-center">
                  <div className="card-body">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Memuat data review CBT...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Error state
  if (error) {
    return (
      <>
        <Breadcrumb title="Review CBT Test" />
        <div className="content">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="card text-center">
                  <div className="card-body">
                    <div className="text-danger mb-3">
                      <i
                        className="isax isax-warning-2"
                        style={{ fontSize: '3rem' }}
                      />
                    </div>
                    <h5 className="text-danger">Error</h5>
                    <p className="mb-4">{error}</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => window.location.reload()}
                    >
                      <i className="isax isax-refresh me-2" />
                      Coba Lagi
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // No questions available
  if (!questions.length) {
    return (
      <>
        <Breadcrumb title="Review CBT Test" />
        <div className="content">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="card text-center">
                  <div className="card-body">
                    <div className="text-warning mb-3">
                      <i
                        className="isax isax-document-text"
                        style={{ fontSize: '3rem' }}
                      />
                    </div>
                    <h5>Tidak ada soal tersedia</h5>
                    <p className="mb-4">
                      CBT ini belum memiliki soal yang dapat direview.
                    </p>
                    <Link
                      to={all_routes.studentCourses}
                      className="btn btn-primary"
                    >
                      <i className="isax isax-home me-2" />
                      Kembali ke Dashboard
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const currentQ = questions[currentQuestion]
  // Find the correct answer index based on is_true property
  const correctAnswerIndex = currentQ.answers.findIndex(
    (answer) => answer.is_true === true,
  )
  const userAnswerId = userAnswerMap.get(currentQ.id)

  // Find the index of the user's selected answer
  const userAnswerIndex = userAnswerId
    ? currentQ.answers.findIndex((answer) => answer.id === userAnswerId)
    : -1

  const isCorrect = userAnswerIndex === correctAnswerIndex

  const getAnswerClass = (optionIndex: number) => {
    if (optionIndex === correctAnswerIndex) {
      return 'border-success bg-success text-white'
    }
    if (optionIndex === userAnswerIndex && !isCorrect) {
      return 'border-danger bg-danger text-white'
    }
    return 'border-secondary'
  }

  const getAnswerIcon = (optionIndex: number) => {
    if (optionIndex === correctAnswerIndex) {
      return 'isax-tick-circle text-success'
    }
    if (optionIndex === userAnswerIndex && !isCorrect) {
      return 'isax-close-circle text-danger'
    }
    return ''
  }

  return (
    <>
      <Breadcrumb title="Review CBT Test" />

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Question Review */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Review Soal {currentQuestion + 1}</h5>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'
                          }`}
                      >
                        {isCorrect ? 'Benar' : 'Salah'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="card-body">

                  {currentQ?.image && (
                    <div className="d-flex align-items-center justify-content-center">
                                  <img className="img-fluid object-fit-cover mb-3" style={{ maxHeight: '400px' }} src={`${process.env.REACT_APP_BASE_URL_API}/storage/${currentQ?.image}`} alt={currentQ?.image} />
                    </div>
                  )}

                  <h6 className="mb-4" dangerouslySetInnerHTML={{ __html: currentQ.question }}></h6>

                  <div className="options-list">
                    {currentQ.answers.map((answer, index) => (
                      <div
                        key={index}
                        className={`option-item p-3 border rounded mb-3 ${getAnswerClass(
                          index,
                        )}`}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className={`option-circle me-3 ${index === correctAnswerIndex
                              ? 'bg-success text-white'
                              : index === userAnswerIndex && !isCorrect
                                ? 'bg-danger text-white'
                                : 'border'
                              }`}
                          >
                            {String.fromCharCode(65 + index)}
                          </div>
                          <span className="flex-grow-1">{answer.choice}</span>
                          {getAnswerIcon(index) && (
                            <i
                              className={`isax ${getAnswerIcon(index)} fs-5`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Explanation */}
                  <div className="mt-4">
                    <h6 className="text-primary">Penjelasan:</h6>
                    <div className="alert alert-info">
                      <p className="mb-0">
                        Jawaban yang benar adalah{' '}
                        <strong>
                          "{currentQ.answers[correctAnswerIndex].choice}"
                        </strong>
                        {!isCorrect && (
                          <span className="text-danger">
                            . Anda menjawab{' '}
                            <strong>
                              "
                              {userAnswerIndex > -1
                                ? currentQ.answers[userAnswerIndex].choice
                                : 'Tidak dijawab'}
                              "
                            </strong>
                          </span>
                        )}
                      </p>
                    </div>
                    {currentQ.discussion ? (
                      <div className="mt-3">
                        <h6 className="text-primary">Pembahasan:</h6>
                        <div className="alert alert-secondary">
                          <p
                            className="mb-0"
                            dangerouslySetInnerHTML={{ __html: currentQ.discussion }}
                          ></p>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Navigation */}
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setCurrentQuestion(currentQuestion - 1)}
                      disabled={currentQuestion === 0}
                    >
                      <i className="isax isax-arrow-left me-2" />
                      Sebelumnya
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={() => setCurrentQuestion(currentQuestion + 1)}
                      disabled={currentQuestion === questions.length - 1}
                    >
                      Selanjutnya
                      <i className="isax isax-arrow-right ms-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Question Navigator */}
            <div className="col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">Navigasi Review</h6>
                </div>
                <div className="card-body">
                  <div className="row g-2">
                    {questions.map((question, index) => {
                      const questionUserAnswerId = userAnswerMap.get(
                        question.id,
                      )
                      const questionUserAnswerIndex = questionUserAnswerId
                        ? question.answers.findIndex(
                          (answer) => answer.id === questionUserAnswerId,
                        )
                        : -1
                      const questionCorrectAnswerIndex = question.answers.findIndex(
                        (answer) => answer.is_true === true,
                      )
                      const questionIsCorrect =
                        questionUserAnswerIndex === questionCorrectAnswerIndex

                      return (
                        <div key={index} className="col-3">
                          <button
                            className={`btn btn-sm w-100 ${index === currentQuestion
                              ? 'btn-primary'
                              : questionIsCorrect
                                ? 'btn-success'
                                : 'btn-danger'
                              }`}
                            onClick={() => setCurrentQuestion(index)}
                          >
                            {index + 1}
                          </button>
                        </div>
                      )
                    })}
                  </div>

                  <div className="mt-3">
                    <small className="text-muted">
                      <span className="badge bg-success me-2">●</span> Jawaban
                      Benar
                      <br />
                      <span className="badge bg-danger me-2">●</span> Jawaban
                      Salah
                      <br />
                      <span className="badge bg-primary me-2">●</span> Soal Saat
                      Ini
                    </small>
                  </div>

                  <div className="mt-4">
                    <Link
                      to={all_routes.studentCourses}
                      className="btn btn-outline-primary w-100"
                    >
                      <i className="isax isax-home me-2" />
                      Kembali ke Dashboard
                    </Link>
                  </div>

                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-success mt-2 w-100"
                      onClick={handleCertificateDownload}
                    >
                      <i className="isax isax-document-download me-2" />
                      Download Sertifikat
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .option-circle {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
      `}</style>

      {/* Modal */}

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateName}
                      onChange={(e) => setCertificateName(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateName.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default CBTReview
